import './App.css';
import { differenceInYears, isWithinInterval, sub } from "date-fns";

/**
 * Calculates and renders a pay code stub based on various date and term-related inputs.
 * This component handles complex calculations for step codes, longevity, and past experience terms.
 * 
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.dropdownVars - An object containing various date and term-related variables.
 * @param {string} props.dropdownVars{'appointed-date'} - The date of appointment.
 * @param {string} props.dropdownVars{'equate-date'} - The equate date for calculations.
 * @param {number} props.dropdownVars{'term-length'} - The total term length.
 * @param {string} props.paycode - The paycode from Step 1.
 * @param {Function} props.handelPayCodeVar - Function to handle paycode variable.
 * @param {Function} props.handleStepCode - Function to handle step code.
 * @param {Function} props.handleStepCodeCurrent - Function to handle current step code.
 * @param {Function} props.handlePastExperience - Function to handle past experience term counter.
 * @param {string} props.title - The title of the pay code stub.
 * @param {string} props.content - The content of the pay code stub.
 * @param {number} props.currentStep - The current step in the process.
 * 
 * @returns {JSX.Element} A React component that displays the pay code stub.
 */
function PayCodeStub(props) {
  let stepCode;
  let stepCodeCurrent;
  let pastExperienceTermCounter = 0;
  let pastExperienceToCurrentTermCounter = 0;
  let stepCodeMax = 8;
  let jobTitle = props.dropdownVars['job-title'];
  let hoursPerWeek = '';

  if(jobTitle === 'school-psychologist' || jobTitle === 'social-worker'){
    stepCodeMax = 7;
  }
  if(jobTitle === 'lab-tech'){
    stepCodeMax = 6;
  }
  if(jobTitle === 'staff-nurse' || jobTitle === 'head-nurse' || jobTitle === 'supervisor-nurse'){
    stepCodeMax = 20;
  }
  if(jobTitle === 'occupational-therapist' || jobTitle === 'physical-therapist' || jobTitle === 'audiologist'){
    stepCodeMax = 10;
  }
  if(jobTitle === 'adult-education-teacher'){
    hoursPerWeek = props.hoursPerWeek;
  }
  
  // If dropdownVars has an appointed-date
  if(props.dropdownVars['appointed-date'] && props.jobTitle != "paraprofessionals"){
    // Date Variables
    let oneYear = 31556926000;
    let currentDate = new Date();
    let appointedDate = new Date(props.dropdownVars['appointed-date']);
    let equateDate = new Date(props.dropdownVars['equate-date']);
    let totalTermLength = props.dropdownVars['term-length'];

    if(isNaN(equateDate)){
      if(totalTermLength){
        if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
            equateDate = sub(appointedDate, { years: (totalTermLength) });
        }
        else{
            equateDate = sub(appointedDate, { years: (totalTermLength / 2) });
        }
      }
      else{
        equateDate = new Date(appointedDate);
      }
    }
    const stepIncreaseDate = new Date();
    stepIncreaseDate.setMonth(2);
    stepIncreaseDate.setDate(1);
    stepIncreaseDate.setHours(0);
    stepIncreaseDate.setMinutes(0);
    stepIncreaseDate.setSeconds(0);
    stepIncreaseDate.setMilliseconds(0);
    let longevity = (currentDate.getTime() - appointedDate.getTime()) / oneYear;
    let startingLongevity = 0;
    let period;
    let periodCurrent;
    let stepCodes = [
      [["A", "B"], 4],
      [["V", "W"], 9],
      [["C", "D"], 12],
      [["G", "H"], 14],
      [["E", "F"], 17],
      [["I", "J"], 19],
      [["K", "L"], 21],
      [["M", "N"], Infinity]
    ];

    // Lab Tech Ends on 6A step, so all longevities are A value. 
    if(jobTitle === 'lab-tech'){
      stepCodes = [
        [["A", "B"], 4],
        [["V", "V"], 9],
        [["C", "C"], 12],
        [["G", "G"], 14],
        [["E", "E"], 17],
        [["I", "I"], 19],
        [["K", "K"], 21],
        [["M", "M"], Infinity]
      ];
    }

    if(jobTitle === 'staff-nurse' || jobTitle === 'head-nurse' || jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
      stepCodes = [
        [["A", "B"], Infinity],
      ];
    }

    // Find numnber of years between appointed and equate dates
    let appointedToEquateDifference = differenceInYears(
      appointedDate,
      equateDate,
    );

    // Find numnber of years between step increase and equate dates from appointed date
    let stepIncreaseToEquateDifference = differenceInYears(
      stepIncreaseDate.setFullYear(appointedDate.getFullYear()),
      equateDate,
    );

    // If step increase date and appointed dates are the same
    if(stepIncreaseToEquateDifference == appointedToEquateDifference){
      parseInt(appointedToEquateDifference++);
    }

    // Set stepIncreaseToEquateDifference variable to larger of two values
    stepIncreaseToEquateDifference = Math.max( parseInt(appointedToEquateDifference), parseInt(stepIncreaseToEquateDifference));

    // Find numnber of years between currrent and equate dates
    let currentToEquateDifference = differenceInYears(
      currentDate,
      equateDate,
    );

    // Find numnber of years between step increase and equate dates from current date
    let currentStepIncreaseToEquateDifference = differenceInYears(
      stepIncreaseDate.setFullYear(currentDate.getFullYear()),
      equateDate,
    );

    // If step increase date and current dates are the same
    if(currentStepIncreaseToEquateDifference == currentToEquateDifference){
      parseInt(currentToEquateDifference++);
    }

    // Set currentToEquateDifference variable to larger of two values
    currentToEquateDifference = Math.max( parseInt(currentToEquateDifference), parseInt(currentStepIncreaseToEquateDifference));

    // Set step increase year to equate date year
    stepIncreaseDate.setFullYear(equateDate.getFullYear());

    // Find number of terms for starting step code
    let stepIncreaseDateAppointed = new Date(stepIncreaseDate);
    let equateDateAppointed = new Date(equateDate);
    for(let i = 0; i <= appointedToEquateDifference; i++){
      let newYear = equateDate.getFullYear() + i;
      if(isWithinInterval(stepIncreaseDateAppointed.setFullYear(newYear), {start: appointedDate,end: equateDate})){
        pastExperienceTermCounter++;
      }
      if(equateDate.getFullYear() != newYear){
        if(isWithinInterval(equateDateAppointed.setFullYear(newYear), {start: appointedDate,end: equateDate})){
          pastExperienceTermCounter++;
        }
      }
    }

    // Find number of terms for current step code
    let stepIncreaseDateToCurrent = new Date(stepIncreaseDate);
    let equateDateToCurrent = new Date(equateDate);
    for(let i = 0; i <= currentToEquateDifference; i++){
      let newYear = equateDate.getFullYear() + i;
      if(isWithinInterval(stepIncreaseDateToCurrent.setFullYear(newYear), {start: currentDate,end: equateDate})){
        pastExperienceToCurrentTermCounter++;
      }
      if(equateDate.getFullYear() != newYear){
        if(isWithinInterval(equateDateToCurrent.setFullYear(newYear), {start: currentDate,end: equateDate})){
          pastExperienceToCurrentTermCounter++;
        }
      }
    }

    // If total term length is odd, add one to both counters
    if(totalTermLength && totalTermLength % 2 != 0){
      pastExperienceTermCounter++;
      pastExperienceToCurrentTermCounter++;
    }

    // Find Starting Step Code
    if(pastExperienceTermCounter <= 0){
      period = 0;
      stepCode = 1;
    }
    for (let i = 1; i <= pastExperienceTermCounter; i++) {
      stepCode = Math.ceil(i / 2);
      if(i === pastExperienceTermCounter) {
        if(i % 2 === 0) {
          period = 0;
          stepCode = stepCode + 1;
        }
        else{
          period = 1;
          stepCode = stepCode;
        }
      }
    }
    
    // Find Current Step Code
    if(pastExperienceToCurrentTermCounter <= 0){
      periodCurrent = 0;
      stepCodeCurrent = 1;
    }
    for (let i = 1; i <= pastExperienceToCurrentTermCounter; i++) {
      stepCodeCurrent = Math.ceil(i / 2);
      if(i === pastExperienceToCurrentTermCounter) {
        if(i % 2 === 0) {
          periodCurrent = 0;
          stepCodeCurrent = stepCodeCurrent + 1;
        }
        else{
          periodCurrent = 1;
          stepCodeCurrent = stepCodeCurrent;
        }
      }
    }

    // Set Step Code Letters
    let stepLetter = stepCodes.find(([_, maxLongevity]) => Math.floor(startingLongevity) <= maxLongevity)[0][period];
    let stepLetterCurrent = stepCodes.find(([_, maxLongevity]) => Math.floor(longevity) <= maxLongevity)[0][periodCurrent];
    // If step code is greater than Max, set it to max and adjust step letter accordingly
    if(stepCode > stepCodeMax){
      stepCode = stepCodeMax;
      stepLetter = stepCodes.find(([_, maxLongevity]) => Math.floor(startingLongevity) <= maxLongevity)[0][1];
    }
    stepCode = stepCode + stepLetter;
    if(stepCodeCurrent > stepCodeMax){
      stepCodeCurrent = stepCodeMax;
      stepLetterCurrent = stepCodes.find(([_, maxLongevity]) => Math.floor(longevity) <= maxLongevity)[0][1];
    }

    // Create Step Code Current variable
    stepCodeCurrent = stepCodeCurrent + stepLetterCurrent;
  }


  // Saves Paycode Sub from Step 1
  if(props.paycode != ''){
    if(!props.hasError){
      props.handleCalculatedPayCodeVar(props.paycode);
    }
    props.handelPayCodeVar(props.paycode);
  }
  else{
    props.handleStepCode(stepCode);
    props.handleStepCodeCurrent(stepCodeCurrent);
    props.handlePastExperience(pastExperienceTermCounter);
    if(props.jobTitle == "paraprofessionals"){
      let currentDate = new Date();
      let appointedDate = new Date(props.dropdownVars['appointed-date']);
      // Find numnber of years between currrent and appointed dates
      let currentToAppointedDifference = differenceInYears(
        currentDate,
        appointedDate,
      );
      stepCodeCurrent = props.paraprofessionalCredits;

      props.handleCurrentToAppointedDifference(currentToAppointedDifference);
      
      if(stepCodeCurrent == "0-5"){
        stepCodeCurrent = "P1";
      }
      else if(stepCodeCurrent == "6-14"){
        stepCodeCurrent = currentToAppointedDifference >= 1 ? "P2" : "P1";
      }
      else if(stepCodeCurrent == "15-29"){
        stepCodeCurrent = "P3";
      }
      else if(stepCodeCurrent == "30-44"){
        stepCodeCurrent = "P4";
      }
      else if(stepCodeCurrent == "45-59"){
        stepCodeCurrent = "P5";
      }
      else if(stepCodeCurrent == "60-89"){
        stepCodeCurrent = currentToAppointedDifference >= 2 ? "P6" : (currentToAppointedDifference >= 1 ? "P2" : "P5");
      }
      else if(stepCodeCurrent.includes("90+")){
        if (stepCodeCurrent.includes("Matriculated") && currentToAppointedDifference >= 2) {
          stepCodeCurrent = "P9A";
        } else if (stepCodeCurrent.includes("Approved") && currentToAppointedDifference >= 1) {
          stepCodeCurrent = "P9B";
        } else if (currentToAppointedDifference >= 2) {
          stepCodeCurrent = "P6";
        } else if (currentToAppointedDifference >= 1) {
          stepCodeCurrent = "P6";
        } else {
          stepCodeCurrent = "P5";
        }
      }
      props.handelPayCodeVar(stepCodeCurrent);
    }
  }

  return (
    <>
      {(props.jobTitle !== 'staff-nurse' && props.jobTitle !== 'head-nurse' && props.jobTitle !== 'supervisor-nurse' && props.jobTitle !== 'occupational-therapist' && props.jobTitle !== 'physical-therapist' && props.jobTitle !== 'audiologist') &&
        <div className={props.title ? 'pay-code-stub full-width' : 'pay-code-stub'}>
          <p className='pay-code'>{props.paycode}{props.currentStep == 1 && props.hoursPerWeek}{props.currentStep == 2 && stepCodeCurrent}</p>
          {props.title && <p className='content'><strong dangerouslySetInnerHTML={{__html: props.title}}></strong></p>}
          {props.content && <p className='content' dangerouslySetInnerHTML={{__html: props.content}}></p>}
        </div>
      }
    </>
  );
}

export default PayCodeStub;